import { SessionConflictCode } from '../hooks/useDeviceSessionStream';

/**
 * device-agent-api allows one live session per device; start rejects with a structured 409
 * (`SESSION_ALREADY_ACTIVE` / `SESSION_ENDING`). open-api attaches it at
 * `extensions.exception.response.code`, but staff-api's proxy client re-throws every open-api GraphQL error
 * as `open-api error: <JSON of the errors>` — so by the time it reaches the browser the code usually lives
 * only inside the message string. Check the structured shape first, then the message (code, then the
 * device-agent-api's human phrasing). Returns null when the error is not a session conflict.
 */
export function readSessionConflict(err: unknown): SessionConflictCode | null {
  const raw =
    (err as { errors?: unknown })?.errors ?? (err as { graphQLErrors?: unknown })?.graphQLErrors;
  const list = Array.isArray(raw) ? raw : [];
  for (const e of list) {
    const code = (
      e as { extensions?: { code?: unknown; exception?: { response?: { code?: unknown } } } }
    )?.extensions;
    const c = code?.exception?.response?.code ?? code?.code;
    if (c === SessionConflictCode.AlreadyActive || c === SessionConflictCode.Ending) return c;
  }
  const texts = [
    (err as { message?: unknown })?.message,
    ...list.map((e) => (e as { message?: unknown })?.message),
  ].filter((m): m is string => typeof m === 'string');
  const text = texts.join('\n');
  if (text.includes(SessionConflictCode.Ending) || text.includes('still shutting down'))
    return SessionConflictCode.Ending;
  if (text.includes(SessionConflictCode.AlreadyActive) || text.includes('already active on this device'))
    return SessionConflictCode.AlreadyActive;
  return null;
}

/** Operator-facing copy for a start conflict. Resume is deliberately not supported in the staff console. */
export function conflictMessage(code: SessionConflictCode): string {
  return code === SessionConflictCode.Ending
    ? 'The previous session on this device is still shutting down. Wait a few seconds, then start again.'
    : 'A session is already running on this device; wait for it to end. Its transcript is in the history below.';
}
